/**
 * Sales order editor — types, constants, and pure helpers.
 * Shell and subcomponents: CreateSalesOrderPage.tsx (~5200 lines).
 * Siblings: salesOrderEditorDirty.ts, salesOrderPaymentTermsApi.ts, SalesOrderApprovalLocationPicker.tsx.
 */
import { ApiError } from "@/lib/api";
import type { ChargeLine, SalesOrderChargeMode } from "@integrator-src/sales-order/sales-order-charge-lines";
import { DEFAULT_SALES_ORDER_SHIPPING_SETTINGS } from "./salesOrderPaymentTermsApi";

export interface SalesOrderDefaultsResponse {
  suggestedNumber: string;
  defaultStatus: string;
  defaultSubmitted: string;
}

export interface SalesOrderCreateResponse {
  ok: boolean;
  order: {
    id: number;
    number: string;
    status: string | null;
    version: number;
  };
  /** Freshsales accounts replayed because the client or end user was set or changed. */
  customerReingestAccountIds?: string[];
}

export interface SalesOrderActivityEntry {
  id: string;
  timestamp: string;
  actorDisplayName: string | null;
  action: string;
  detail: string;
}

export interface SalesOrderAttachment {
  id: string;
  bucket: string;
  path: string;
  fileName: string;
  mimeType: string | null;
  size: number | null;
  uploadedAt: string;
  kind: string;
  downloadUrl: string;
  localUrl?: string;
  localFile?: File;
}

export interface SalesOrderAttachmentUploadResponse {
  attachments: SalesOrderAttachment[];
}

export interface SalesOrderApprovalDocumentsResponse {
  ticketId: string | null;
  freshdeskLink: string | null;
  freshdeskTicketId: number | null;
  documents: Array<{
    id: string;
    kind: "email_thread" | "approval_attachment";
    kindLabel: string;
    fileName: string;
    contentType: string | null;
    downloadUrl: string | null;
    textBody: string | null;
  }>;
}

export type PoViewerDocumentKind = "order_attachment" | "email_thread" | "approval_attachment";

export interface PoViewerDocument {
  id: string;
  kind: PoViewerDocumentKind;
  kindLabel: string;
  fileName: string;
  mimeType: string | null;
  downloadUrl: string | null;
  localUrl?: string;
  localFile?: File;
  textBody: string | null;
  orderAttachment?: SalesOrderAttachment;
}

export interface SalesOrderDetailResponse {
  order: {
    id: number;
    number: string;
    mvId: string | null;
    status: string | null;
    version: number;
    submitted: string | null;
    shipByDate: string | null;
    customerPurchaseOrderNumber: string | null;
    paymentTerms: string | null;
    locationId: string | null;
    locationAbbreviation: string | null;
    clientFsAccountId: string | null;
    endUserFsAccountId: string | null;
    shipToRecipient: string | null;
    shipToAttention: string | null;
    shipToAddressLine1: string | null;
    shipToAddressLine2: string | null;
    shipToAddressLine3: string | null;
    shipToCity: string | null;
    shipToState: string | null;
    shipToZip: string | null;
    shipToCountry: string | null;
    billToRecipient: string | null;
    billToLine1: string | null;
    billToLine2: string | null;
    billToLine3: string | null;
    billToCity: string | null;
    billToState: string | null;
    billToZip: string | null;
    billToCountry: string | null;
    specialInstructions: string | null;
    pilotProgram: boolean;
    salesManagerName: string | null;
    repAgencyId: string | null;
    repAgencyName: string | null;
    repStaffId: string | null;
    repStaffName: string | null;
    repSyncAt: string | null;
    repSyncFreshdeskTicketId: number | null;
    repSplits: Array<{
      position: number;
      repAgencyId: string;
      name: string;
      shareNumerator: number;
      shareDenominator: number;
    }>;
    attachments: SalesOrderAttachment[];
    linkStatus: "ready" | "pending";
    shippingChargeMode: SalesOrderChargeMode;
    magnetChargeMode: SalesOrderChargeMode;
    trackingRecipientEmails: string[];
    invoiceRecipientEmails: string[];
    approvalTicketId: string | null;
    hasProductionBatchItems: boolean;
  };
  items: Array<{
    id: number;
    sku: string;
    templateId: number | null;
    description: string | null;
    quantity: number;
    unitPrice: number;
    remark: string | null;
    origin?: ChargeLine["origin"] | null;
  }>;
  invoices?: Array<{
    mvId: string;
    number: string;
  }>;
  activity: SalesOrderActivityEntry[];
}

export interface SalesOrderEditLockState {
  resourceType: string;
  resourceId: string;
  status: "available" | "owned" | "held";
  isOwner: boolean;
  holder: {
    userId: string;
    displayName: string;
    acquiredAt: string;
    heartbeatAt: string;
  } | null;
  lockToken: string | null;
  expiresAt: string | null;
}

export interface SalesOrderEditLockResponse {
  lock: SalesOrderEditLockState;
}

export function isSalesOrderVersionConflict(error: unknown): error is ApiError {
  if (!(error instanceof ApiError) || error.status !== 409 || !error.body || typeof error.body !== "object") {
    return false;
  }
  return (error.body as { code?: unknown }).code === "SALES_ORDER_VERSION_CONFLICT";
}

export function isSalesOrderEditLockConflict(error: unknown): error is ApiError {
  if (!(error instanceof ApiError) || error.status !== 409 || !error.body || typeof error.body !== "object") {
    return false;
  }
  return (error.body as { code?: unknown }).code === "SALES_ORDER_EDIT_LOCK_CONFLICT";
}

export interface SalesOrderPartyOption {
  kind: "client" | "end-user";
  label: string;
  fsAccountId: string | null;
  state: string | null;
  salesManagerName?: string | null;
  salesManagerEmail?: string | null;
  salesManagerError?: string | null;
  repAgencyId?: string | null;
  repAgencyName?: string | null;
  repAgencyError?: string | null;
  repStaffId?: string | null;
  repStaffName?: string | null;
  repStaffWarning?: string | null;
  magnetExemptions?: string[];
  stocking?: string | null;
  shippingFeeExempt?: boolean;
  accountsPayableEmail?: string | null;
  billTo: {
    recipient: string;
    line1: string;
    line2: string;
    line3: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  } | null;
}

export interface SalesOrderPartyCatalogResponse {
  clients: SalesOrderPartyOption[];
  endUsers: SalesOrderPartyOption[];
}

export interface SalesOrderPaymentTermsOption {
  value: string;
  label: string;
}

export interface SalesOrderPaymentTermsOptionsResponse {
  options: SalesOrderPaymentTermsOption[];
}

export interface SalesOrderLocationOption {
  id: number;
  name: string;
  abbreviation: string | null;
}

export interface SalesOrderLocationsResponse {
  locations: SalesOrderLocationOption[];
}

export interface SalesOrderSkuOption {
  sku: string;
  description: string;
  variant: string;
  megaventoryId: string;
  templateId: number | null;
}

export interface SalesOrderSkuOptionsResponse {
  options: SalesOrderSkuOption[];
}

export interface SalesOrderSkuDescriptionResponse {
  sku: string;
  description: string | null;
  descriptionError: string | null;
}

export interface SalesOrderEndUserHistoryResponse {
  hasPriorOrder: boolean;
  items: Array<{
    salesOrderId: number;
    salesOrderNumber: string;
    submitted: string | null;
    sku: string;
    quantity: number;
    unitPrice: number;
  }>;
}

export interface SalesOrderCustomerPoDuplicatesResponse {
  orders: Array<{
    id: number;
    number: string;
  }>;
}

export interface SalesOrderEndUserQuotesResponse {
  items: Array<{
    quoteId: number;
    quoteNumber: string;
    validThrough: string | null;
    sku: string;
    quantity: number;
    unitPrice: number;
  }>;
}

export interface SalesOrderAddressHistoryResponse {
  clientBillTo: {
    recipient: string | null;
    line1: string | null;
    line2: string | null;
    line3?: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
  } | null;
  endUserShipTo: {
    recipient: string | null;
    attention?: string | null;
    line1: string | null;
    line2: string | null;
    line3?: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
  } | null;
}

export interface SalesOrderPoAnalysisResponse {
  status: "ready";
  fileName: string;
  warnings: string[];
  historicalContext: SalesOrderAddressHistoryResponse;
  extracted: {
    customerPo: string | null;
    specialInstructions: string | null;
    clientName: string | null;
    clientFsAccountId?: string | null;
    endUserName: string | null;
    endUserFsAccountId?: string | null;
    billTo: {
      recipient: string | null;
      line1: string | null;
      line2: string | null;
      line3: string | null;
      city: string | null;
      state: string | null;
      zip: string | null;
      country: string | null;
    };
    shipTo: {
      recipient: string | null;
      attention: string | null;
      line1: string | null;
      line2: string | null;
      city: string | null;
      state: string | null;
      zip: string | null;
      country: string | null;
    };
    items: Array<{
      rawSku: string | null;
      matchedSku: string | null;
      quantity: number | null;
      unitPrice: number | null;
      description: string | null;
      confidence: number | null;
      warning: string | null;
    }>;
  };
}

export interface SalesOrderAnalysisLogEntry {
  id: number;
  timestamp: string;
  level: "info" | "error";
  message: string;
}

export interface SalesOrderItemDraft {
  draftId: string;
  sku: string;
  templateId: number | null;
  productDescription: string;
  productDescriptionError: string;
  quantity: string;
  unitPrice: string;
  remark: string;
  showRemark: boolean;
  megaventoryId: string;
  origin: ChargeLine["origin"];
  /** Editor-only. The line is shown struck as waived and is not saved on the order. */
  waivedPreview?: boolean;
}

export const EMPTY_ITEM: Omit<SalesOrderItemDraft, "draftId"> = {
  sku: "",
  templateId: null,
  productDescription: "",
  productDescriptionError: "",
  quantity: "1",
  unitPrice: "",
  remark: "",
  showRemark: false,
  megaventoryId: "",
  origin: "user",
};

export const DEFAULT_SHIPPING_SKU = DEFAULT_SALES_ORDER_SHIPPING_SETTINGS.sku;
export const EMPTY_MAGNET_EXEMPTIONS: string[] = [];
